import { useGT } from 'gt-react';
import { z } from 'zod';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

// Mirrors the backend render_stat_tiles inputSchema. Local (not shared via
// @erp/rpc) because it's tiny and this is the only consumer.
const statTilesArgsSchema = z.object({
  title: z.string().optional(),
  tiles: z
    .array(
      z.object({
        label: z.string(),
        value: z.string(),
        hint: z.string().optional(),
        intent: z.enum(['positive', 'negative', 'neutral']).optional(),
      }),
    )
    .min(1),
});

const INTENT_CLASS: Record<'positive' | 'negative' | 'neutral', string> = {
  positive: 'text-emerald-600 dark:text-emerald-400',
  negative: 'text-red-600 dark:text-red-400',
  neutral: 'text-card-foreground',
};

interface StatTilesToolResultProps {
  /** The `render_stat_tiles` tool-call args: `{ title?, tiles }`. */
  args: Record<string, unknown>;
}

/**
 * Inline generative-UI card: renders the AI's `render_stat_tiles` spec as a
 * responsive row of KPI tiles (Tailwind + shadcn Card only). Values are
 * pre-formatted strings supplied by the model.
 */
export function StatTilesToolResult({ args }: StatTilesToolResultProps) {
  const gt = useGT();
  const parsed = statTilesArgsSchema.safeParse(args);

  if (!parsed.success || parsed.data.tiles.length === 0) {
    return (
      <Card>
        <p className="text-muted-foreground text-sm">
          {gt('Unable to render stats.', {
            context:
              'shown when an AI-generated stat tiles spec is invalid or has no data',
          })}
        </p>
      </Card>
    );
  }

  const { title, tiles } = parsed.data;

  return (
    <Card>
      {title && (
        <div className="text-card-foreground text-sm font-medium">{title}</div>
      )}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {tiles.map((tile, i) => (
          <div
            key={`${tile.label}-${i}`}
            className="flex min-w-0 flex-col gap-0.5 rounded-lg border p-3"
          >
            <span
              className="text-muted-foreground truncate text-xs"
              title={tile.label}
            >
              {tile.label}
            </span>
            <span
              className={cn(
                'truncate text-xl font-semibold tabular-nums',
                INTENT_CLASS[tile.intent ?? 'neutral'],
              )}
              title={tile.value}
            >
              {tile.value}
            </span>
            {tile.hint && (
              <span className="text-muted-foreground text-xs">{tile.hint}</span>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
